import { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
  ShieldCheck,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Shield,
  Scale,
  FileCheck,
  ChevronDown,
  ChevronUp,
  X,
  Database,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { StatusBadge } from "../components/StatusBadge";
import { API_URL } from "../../constants/index";
import { getAPI } from "../../api/frameworksApi";
import type {
  CisoEnvelope,
  CisoAlertsData,
  CisoKpis,
  CompliancePostureData,
  BuHeatmapData,
  ComplianceDriftData,
  CisoRisksData,
  AuditReadinessData,
  ExceptionsSummaryData,
  TrendPoint,
} from "../types/cisoDashboard";

/* ── Helpers ────────────────────────────────────────────────────────────── */

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatMonth(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short" });
}

function complianceColor(pct: number): string {
  if (pct >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (pct >= 50) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function complianceBg(pct: number): string {
  if (pct >= 80) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-red-500";
}

function heatCellBg(code: string): string {
  switch (code) {
    case "GREEN":
      return "bg-emerald-500 text-white";
    case "YELLOW":
      return "bg-amber-400 text-[#0f172a]";
    case "RED":
      return "bg-red-500 text-white";
    default:
      return "bg-slate-300 dark:bg-slate-600 text-white";
  }
}

function formatStatusLabel(v: string) {
  return v
    .split(/[_-]/g)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

const sevBadge: Record<string, { dot: string; text: string }> = {
  critical: { dot: "bg-red-500", text: "text-red-600 dark:text-red-400" },
  high: { dot: "bg-orange-500", text: "text-orange-600 dark:text-orange-400" },
  medium: { dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  low: { dot: "bg-slate-400", text: "text-slate-500 dark:text-white/40" },
};

function PanelState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-6 py-10 text-center">
      <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-white/[0.06] dark:text-white/40">
        <Database className="size-4" />
      </div>
      <p className="text-sm font-bold text-[#0f172a] dark:text-white">
        {title}
      </p>
      <p className="mt-1 text-xs text-[#64748b] dark:text-white/40">{body}</p>
    </div>
  );
}

/* ── Sparkline (inline SVG) ─────────────────────────────────────────────── */

function Sparkline({
  points,
  color = "#06b6d4",
  width = 100,
  height = 24,
}: {
  points: TrendPoint[];
  color?: string;
  width?: number;
  height?: number;
}) {
  if (points.length < 2) return null;
  const vals = points.map((p) => p.compliancePercent);
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const rng = max - min || 1;
  const pts = vals
    .map(
      (v, i) =>
        `${(i / (vals.length - 1)) * width},${height - ((v - min) / rng) * height * 0.75 - height * 0.12}`,
    )
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ── Custom Recharts Tooltip ────────────────────────────────────────────── */

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-[#131320] border border-[#e2e8f0] dark:border-white/[0.08] rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-bold text-[#0f172a] dark:text-white mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: p.color }}
          />
          <span className="text-[#64748b] dark:text-white/40">{p.name}:</span>
          <span className="font-bold text-[#0f172a] dark:text-white">
            {p.value}%
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Donut Chart (SVG) ──────────────────────────────────────────────────── */

function DonutChart({
  percent,
  size = 80,
}: {
  percent: number;
  size?: number;
}) {
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        className="text-[#f1f5f9] dark:text-white/[0.06]"
        strokeWidth={10}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={
          percent >= 80 ? "#10b981" : percent >= 50 ? "#f59e0b" : "#ef4444"
        }
        strokeWidth={10}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="transition-all duration-1000"
      />
      <text
        x={size / 2}
        y={size / 2 + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-[#0f172a] dark:fill-white text-[16px] font-black"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {Math.round(percent)}%
      </text>
    </svg>
  );
}

/* ── Chart Colors ───────────────────────────────────────────────────────── */

const CHART_COLORS = [
  "#6366f1",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#06b6d4",
  "#8b5cf6",
];

/* ══════════════════════════════════════════════════════════════════════════
   CISO Dashboard
   ══════════════════════════════════════════════════════════════════════════ */

export function CISODashboard({ hideTitle = false }: { hideTitle?: boolean } = {}) {
  const [alerts, setAlerts] = useState<CisoAlertsData | null>(null);
  const [kpis, setKpis] = useState<CisoKpis | null>(null);
  const [posture, setPosture] = useState<CompliancePostureData | null>(null);
  const [heatmap, setHeatmap] = useState<BuHeatmapData | null>(null);
  const [drift, setDrift] = useState<ComplianceDriftData | null>(null);
  const [risks, setRisks] = useState<CisoRisksData | null>(null);
  const [audit, setAudit] = useState<AuditReadinessData | null>(null);
  const [exceptions, setExceptions] = useState<ExceptionsSummaryData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<number>>(
    new Set(),
  );
  const [expandedRisk, setExpandedRisk] = useState<string | null>(null);
  const [hoverCell, setHoverCell] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const results = await Promise.allSettled([
        getAPI(API_URL.CISO_ALERTS),
        getAPI(API_URL.CISO_KPIS),
        getAPI(API_URL.CISO_COMPLIANCE_POSTURE),
        getAPI(API_URL.CISO_BU_HEATMAP),
        getAPI(API_URL.CISO_COMPLIANCE_DRIFT),
        getAPI(API_URL.CISO_RISKS),
        getAPI(API_URL.CISO_AUDIT_READINESS),
        getAPI(API_URL.CISO_EXCEPTIONS_SUMMARY),
      ]);
      if (cancelled) return;
      const get = <T,>(i: number): T | null =>
        results[i].status === "fulfilled"
          ? ((results[i] as PromiseFulfilledResult<CisoEnvelope<T>>).value
              ?.data ?? null)
          : null;
      setAlerts(get<CisoAlertsData>(0));
      setKpis(get<CisoKpis>(1));
      setPosture(get<CompliancePostureData>(2));
      setHeatmap(get<BuHeatmapData>(3));
      setDrift(get<ComplianceDriftData>(4));
      setRisks(get<CisoRisksData>(5));
      setAudit(get<AuditReadinessData>(6));
      setExceptions(get<ExceptionsSummaryData>(7));
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading)
    return (
      <PanelState
        title="Loading CISO Dashboard"
        body="Fetching organizational security posture data."
      />
    );

  const visibleAlerts = (alerts?.alerts ?? []).filter(
    (_, i) => !dismissedAlerts.has(i),
  );
  const totalRisks =
    (kpis?.criticalRiskCount ?? 0) +
    (kpis?.highRiskCount ?? 0) +
    (kpis?.mediumRiskCount ?? 0) +
    (kpis?.lowRiskCount ?? 0);

  // Build drift chart data
  const driftChartData = (() => {
    if (!drift?.trends?.length) return [];
    const months = drift.trends[0].trendPoints.map((p) =>
      formatMonth(p.measuredAt),
    );
    return months.map((m, i) => {
      const row: Record<string, unknown> = { month: m };
      drift.trends.forEach((t) => {
        row[t.systemName] = t.trendPoints[i]?.compliancePercent ?? 0;
      });
      return row;
    });
  })();

  return (
    <div className="space-y-5">
      {/* ── Page Header ── */}
      {!hideTitle && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="size-4 text-indigo-500 dark:text-indigo-400" />
            <p className="text-[10px] font-black tracking-[0.12em] text-indigo-500 dark:text-indigo-400">
              CISO
            </p>
          </div>
          <h1>Security Posture Dashboard</h1>
          <p className="text-xs text-[#64748b] dark:text-white/40 mt-0.5">
            Strategic compliance, risk landscape, and audit readiness across the
            organization.
          </p>
        </div>
      )}

      {/* ══ 1. Alert Banner ══ */}
      {/* {visibleAlerts.length > 0 && (
        <div className="space-y-2">
          {visibleAlerts.slice(0, 3).map((alert, i) => {
            const isCrit = alert.severity === "critical";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${isCrit ? "bg-red-50 dark:bg-red-500/[0.06] border-red-200/60 dark:border-red-500/20" : "bg-amber-50 dark:bg-amber-500/[0.06] border-amber-200/60 dark:border-amber-500/20"}`}
              >
                <span className="text-base flex-shrink-0">
                  {isCrit ? "\u26A0\uFE0F" : "\uD83D\uDD36"}
                </span>
                <p
                  className={`text-xs font-medium flex-1 ${isCrit ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300"}`}
                >
                  {alert.message}
                </p>
                <span className="text-[10px] text-[#94a3b8] dark:text-white/30 flex-shrink-0">
                  {relativeTime(alert.detectedAt)}
                </span>
                <button
                  onClick={() => setDismissedAlerts((s) => new Set(s).add(i))}
                  className="text-[#94a3b8] dark:text-white/30 hover:text-[#64748b] dark:hover:text-white/50 flex-shrink-0"
                >
                  <X className="size-3.5" />
                </button>
              </motion.div>
            );
          })}
        </div>
      )} */}

      {/* ══ 2. KPI Strip ══ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Org Compliance */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] border-l-indigo-500 p-4"
        >
          <div className="flex items-center gap-3">
            <DonutChart percent={kpis?.orgCompliancePercent ?? 0} size={52} />
            <div>
              <p className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30">
                Org Compliance
              </p>
              <p className="text-lg font-black text-[#0f172a] dark:text-white">
                {(kpis?.orgCompliancePercent ?? 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </motion.div>

        {/* Open Risks */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] border-l-red-500 p-4"
        >
          <p className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 mb-1">
            Open Risks
          </p>
          <p className="text-2xl font-black text-[#0f172a] dark:text-white leading-none mb-2">
            {totalRisks}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[
              {
                c: kpis?.criticalRiskCount,
                l: "Crit",
                cls: "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400",
              },
              {
                c: kpis?.highRiskCount,
                l: "High",
                cls: "bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400",
              },
              {
                c: kpis?.mediumRiskCount,
                l: "Med",
                cls: "bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400",
              },
              {
                c: kpis?.lowRiskCount,
                l: "Low",
                cls: "bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/40",
              },
            ].map((r) => (
              <span
                key={r.l}
                className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${r.cls}`}
              >
                {r.c ?? 0} {r.l}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Audit Readiness */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] border-l-amber-500 p-4"
        >
          <p className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 mb-1">
            Audit Readiness
          </p>
          <p className="text-2xl font-black text-[#0f172a] dark:text-white leading-none mb-2">
            {(kpis?.auditReadinessPercent ?? 0).toFixed(1)}%
          </p>
          <div className="h-1.5 bg-[#f1f5f9] dark:bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${complianceBg(kpis?.auditReadinessPercent ?? 0)} transition-all duration-1000`}
              style={{ width: `${kpis?.auditReadinessPercent ?? 0}%` }}
            />
          </div>
        </motion.div>

        {/* Exceptions */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] border-l-violet-500 p-4"
        >
          <p className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 mb-1">
            Exceptions
          </p>
          <p className="text-2xl font-black text-[#0f172a] dark:text-white leading-none mb-1">
            {kpis?.exceptionsCount ?? 0}
          </p>
          <p className="text-[10px] text-[#94a3b8] dark:text-white/30">
            active risk exceptions
          </p>
        </motion.div>

        {/* Posture Trend */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] border-l-emerald-500 p-4"
        >
          <p className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 mb-1">
            Posture Trend
          </p>
          <div className="flex items-center gap-2">
            {kpis?.postureTrend === "IMPROVING" && (
              <TrendingUp className="size-7 text-emerald-500" />
            )}
            {kpis?.postureTrend === "DECLINING" && (
              <TrendingDown className="size-7 text-red-500" />
            )}
            {kpis?.postureTrend === "STABLE" && (
              <Minus className="size-7 text-[#94a3b8]" />
            )}
            <div>
              <p
                className={`text-sm font-black ${kpis?.postureTrend === "IMPROVING" ? "text-emerald-600 dark:text-emerald-400" : kpis?.postureTrend === "DECLINING" ? "text-red-600 dark:text-red-400" : "text-[#94a3b8] dark:text-white/40"}`}
              >
                {kpis?.postureTrend === "IMPROVING"
                  ? "Improving"
                  : kpis?.postureTrend === "DECLINING"
                    ? "Declining"
                    : "Stable"}
              </p>
              <p className="text-[10px] text-[#94a3b8] dark:text-white/30">
                vs. last 30 days
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ══ 3 + 4. Framework Posture (60%) + BU Heatmap (40%) ══ */}
      <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-5">
        {/* Framework Alignment */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#f1f5f9] dark:border-white/[0.04]">
            <div className="flex items-center gap-2">
              <BarChart3 className="size-3.5 text-indigo-500 dark:text-indigo-400" />
              <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
                Framework Alignment
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#f1f5f9] dark:border-white/[0.04]">
                  {["System", "Current", "Target", "Delta", "Trend"].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-center px-4 py-2.5 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {(posture?.frameworks ?? []).map((fw) => (
                  <tr
                    key={fw.sspId}
                    className="border-b border-[#f8f9fc] dark:border-white/[0.03]"
                  >
                    <td className="px-4 py-3 font-medium text-[#0f172a] dark:text-white/80">
                      {fw.systemName}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#f1f5f9] dark:bg-white/[0.06] rounded-full overflow-hidden max-w-[120px]">
                          <div
                            className={`h-full rounded-full ${complianceBg(fw.currentPercent)}`}
                            style={{ width: `${fw.currentPercent}%` }}
                          />
                        </div>
                        <span
                          className={`font-bold font-mono ${complianceColor(fw.currentPercent)}`}
                        >
                          {fw.currentPercent.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#94a3b8] dark:text-white/30 font-mono">
                      {fw.targetPercent}%
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`font-bold ${fw.pointDelta >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
                      >
                        {fw.pointDelta >= 0 ? "+" : ""}
                        {fw.pointDelta.toFixed(1)} pts
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Sparkline
                        points={fw.trendPoints}
                        color={fw.pointDelta >= 0 ? "#10b981" : "#ef4444"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {posture && (
            <div className="px-5 py-3 border-t border-[#f1f5f9] dark:border-white/[0.04] flex items-center justify-between">
              <span className="text-xs font-bold text-[#0f172a] dark:text-white/70">
                Overall: {posture.overallCompliancePercent.toFixed(1)}%
              </span>
              <span className="text-[10px] text-[#94a3b8] dark:text-white/30">
                {posture.totalFrameworks} systems
              </span>
            </div>
          )}
        </motion.div>

        {/* BU Heatmap */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
        >
          <div className="px-5 py-3.5 border-b border-[#f1f5f9] dark:border-white/[0.04]">
            <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
              Business Unit Compliance
            </h3>
            <p className="text-[10px] text-[#94a3b8] dark:text-white/30 mt-0.5">
              hover cells for details
            </p>
          </div>
          {heatmap && (
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left px-2 py-2 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30">
                      Unit
                    </th>
                    {heatmap.systemNames.map((s) => (
                      <th
                        key={s}
                        className="text-center px-2 py-2 text-[9px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 max-w-[90px] truncate"
                        title={s}
                      >
                        {s.split(" ").slice(0, 2).join(" ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmap.rows.map((row, ri) => (
                    <tr key={row.orgUnitId}>
                      <td className="px-2 py-2 font-medium text-[#0f172a] dark:text-white/70 whitespace-nowrap">
                        {row.orgUnitName}
                      </td>
                      {row.cells.map((cell, ci) => {
                        const key = `${ri}-${ci}`;
                        const isHover = hoverCell === key;
                        return (
                          <td
                            key={ci}
                            className="px-2 py-2 text-center relative"
                            onMouseEnter={() => setHoverCell(key)}
                            onMouseLeave={() => setHoverCell(null)}
                          >
                            <div
                              className={`inline-flex items-center justify-center w-12 h-7 rounded text-[11px] font-bold transition-transform ${isHover ? "scale-110" : ""} ${heatCellBg(cell.colorCode)}`}
                            >
                              {Math.round(cell.compliancePercent)}%
                            </div>
                            {isHover && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#1a2130] border border-[#2a3448] rounded-lg px-3 py-2 text-[10px] text-white whitespace-nowrap z-10 shadow-lg">
                                <p className="font-bold">
                                  {row.orgUnitName} x {cell.systemName}
                                </p>
                                <p className="text-white/60">
                                  {cell.compliancePercent.toFixed(1)}%
                                </p>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Legend */}
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#f1f5f9] dark:border-white/[0.04]">
                {[
                  { l: "\u226580%", cls: "bg-emerald-500" },
                  { l: "50-79%", cls: "bg-amber-400" },
                  { l: "<50%", cls: "bg-red-500" },
                  { l: "No Data", cls: "bg-slate-300 dark:bg-slate-600" },
                ].map((item) => (
                  <div key={item.l} className="flex items-center gap-1.5">
                    <span className={`size-2.5 rounded ${item.cls}`} />
                    <span className="text-[9px] text-[#94a3b8] dark:text-white/30">
                      {item.l}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* ══ 5. Compliance Drift ══ */}
      {drift && drift.trends.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#f1f5f9] dark:border-white/[0.04]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
                Historic Compliance Drift
              </h3>
              {drift.systemsWithDriftWarning > 0 && (
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400">
                  {drift.systemsWithDriftWarning} drift warning
                  {drift.systemsWithDriftWarning > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <span className="text-[10px] text-[#94a3b8] dark:text-white/30">
              6-month trend
            </span>
          </div>
          <div className="px-5 py-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={driftChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(148,163,184,0.1)"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 100]}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {drift.trends.map((t, i) => (
                  <Area
                    key={t.sspId}
                    type="monotone"
                    dataKey={t.systemName}
                    name={t.systemName}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    fillOpacity={0.08}
                    strokeWidth={t.driftWarning ? 2.5 : 1.5}
                    strokeDasharray={t.driftWarning ? "6 3" : undefined}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-[#94a3b8] dark:text-white/30 mt-2">
              {drift.systemsWithDriftWarning} of {drift.totalSystems} systems
              with drift warning
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
